/* eslint-disable react-hooks/rules-of-hooks */
import { observer } from "mobx-react";
import {
  Flex,
  Text,
} from "@mantine/core";
import { useStore } from "../../../../hooks/use-store";
import ImageObserved from "../Image";
import { PromptField } from "./PromptField";
import { PipelineParameters } from "../Pipeline";
import { priceMap } from "../Router";
import orbitGif from "../images/Orbit.gif";
import craneGif from "../images/Crane_Lift.gif";
import dollyGif from "../images/Dolly_Zoom.gif";
import carFollowGif from "../images/Side_Tracking.gif";
import heroGif from "../images/Hero_Descent.gif";
import timeLapseGif from "../images/timelapse.gif";

const Element = (model: {
  buy: (params: PipelineParameters[]) => Promise<void>;
  loading: boolean
}) => {

  const {
    dataStore: { spaceStore },
  } = useStore();

  return (
    <Flex direction={'column'} w="100%" pos='relative' gap={12} mt={20} mb={60}>
      <Flex
        style={{
          position: 'absolute',
          top: '0',
          left: '50%',
          transform: 'translate(420px, 0)',
        }}
      >
        <Flex
          direction="column"
          gap={12}
          style={{
            width: '360px',
            position: 'relative',
            padding: '18px',
            backgroundColor: 'rgba(32, 32, 32, 1)',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}
        >
          <Flex gap={8}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 1.96973H8C4 1.96973 2 3.96973 2 7.96973V12.9697C2 17.9697 4 18.9697 8 18.9697H8.5C8.78 18.9697 9.14 19.1497 9.3 19.3697L10.8 21.3697C11.46 22.2497 12.54 22.2497 13.2 21.3697L14.7 19.3697C14.89 19.1197 15.19 18.9697 15.5 18.9697H16C20 18.9697 22 16.9697 22 12.9697V7.96973C22 3.96973 20 1.96973 16 1.96973ZM8.53 12.1697C8.82 12.4597 8.82 12.9397 8.53 13.2297C8.38 13.3797 8.19 13.4497 8 13.4497C7.81 13.4497 7.62 13.3797 7.47 13.2297L5.47 11.2297C5.18 10.9397 5.18 10.4597 5.47 10.1697L7.47 8.16973C7.76 7.87973 8.24 7.87973 8.53 8.16973C8.82 8.45973 8.82 8.93973 8.53 9.22973L7.06 10.6997L8.53 12.1697ZM13.69 8.65973L11.69 13.3297C11.57 13.6097 11.29 13.7797 11 13.7797C10.9 13.7797 10.8 13.7597 10.7 13.7197C10.32 13.5597 10.14 13.1197 10.31 12.7297L12.31 8.05973C12.47 7.67973 12.91 7.49973 13.3 7.66973C13.68 7.83973 13.85 8.27973 13.69 8.65973ZM18.53 11.2297L16.53 13.2297C16.38 13.3797 16.19 13.4497 16 13.4497C15.81 13.4497 15.62 13.3797 15.47 13.2297C15.18 12.9397 15.18 12.4597 15.47 12.1697L16.94 10.6997L15.47 9.22973C15.18 8.93973 15.18 8.45973 15.47 8.16973C15.76 7.87973 16.24 7.87973 16.53 8.16973L18.53 10.1697C18.82 10.4597 18.82 10.9397 18.53 11.2297Z" fill="#BDBDBD" />
            </svg>


            <Text color="white" size={'sm'} fw={700}>
              Effects examples
            </Text>
          </Flex>
          <Text color="rgba(189, 189, 189, 1)" size={'sm'}>
            Click to apply
          </Text>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
            {[
              {
                title: 'Orbit',
                prompt:
                  'A cinematic smooth orbit shot around a parked vehicle. The camera keeps the car centered in the frame. Smooth, steady motion, with realistic depth, natural reflections, and dynamic lighting.',
                image: orbitGif
              },
              {
                title: 'Crane Lift',
                prompt:
                  'A crane-style camera movement starting low near the front bumper of the car and rising smoothly upward to a wide overhead shot. Smooth cinematic motion, natural lighting, and soft focus transitions.',
                image: craneGif
              },
              {
                title: 'Dolly Zoom',
                prompt:
                  'A dramatic dolly zoom shot of a car. The camera slowly pushes in while the background pulls out, creating a cinematic vertigo effect. Smooth focus transition, emphasizing the car’s design details and contours.',
                image: dollyGif
              },
              {
                title: 'Car Follow',
                prompt:
                  'Camera follows the fast driving car as if it is mounted on a drone or crane. Slight parallax in the background, motion blur, realistic reflections on the car surface, cinematic depth of field.',
                image: carFollowGif
              },
              {
                title: 'Hero Descent',
                prompt:
                  'A cinematic hero shot of a car in an advertising style. The camera starts high above the car, looking down, then smoothly descends in a crane-like motion while tilting to keep the car perfectly centered and in sharp focus. The movement reveals the environment first, then emphasizes the car as the main subject. Final framing is at eye-level with a slight low angle, making the car appear powerful and iconic. Ultra-smooth motion, professional commercial look, dramatic lighting, high detail.',
                image: heroGif
              },
              {
                title: 'Time Lapse',
                prompt: 'A time lapse of the landscape from day to night.',
                image: timeLapseGif
              }
            ].map(({ title, prompt, image }) => (
              <div
                key={title}
                onClick={() => {
                  spaceStore.setPrompt(prompt);
                }}
                style={{
                  cursor: 'pointer',
                  // border: '1px solid rgba(189, 189, 189, 0.2)',
                  borderRadius: '6px',
                  overflow: 'hidden',
                  height: '140px',
                  position: 'relative',
                  backgroundImage: `url(${image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  display: 'flex',
                  alignItems: 'flex-end'
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.65), rgba(0,0,0,0))',
                    padding: '12px'
                  }}
                >
                  <Text color="white" size={'sm'} fw={700}>
                    {title}
                  </Text>
                  {/* <div style={{ maxWidth: '400px' }}>
                    <Text
                      color="rgba(255, 255, 255, 0.85)"
                      size={'xs'}
                      style={{
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {prompt}
                    </Text>
                  </div> */}
                </div>
              </div>
            ))}
          </div>
        </Flex>
      </Flex>
      <ImageObserved
        src={spaceStore.selectedImage ?? undefined}
        width="100%"
        height="auto"
        imgStyle={{ borderRadius: '6px', overflow: 'hidden' }}
      >
      </ImageObserved>
      <Flex
        style={{
          position: 'absolute',
          right: '-320px',
          top: '300px',
          width: '300px',
          zIndex: 1000,
        }}
        id="sidebar"
      >
      </Flex>
      <PromptField
        credits={priceMap.KLING}
        onClick={async () => {
          const pipeline: PipelineParameters = {
            provider: 'custom',
            type: 'kling',
            inputSrc: spaceStore.selectedImage!,
            prompt: spaceStore.prompt,
            credits: priceMap.KLING,
            srcset: [],
          }

          await model.buy([pipeline]);
        }}
        value={spaceStore.prompt}
        onChangeEnd={(value) => {
          spaceStore.setPrompt(value)
        }}
        loading={model.loading}
        placeholder="Describe an video and click generate"
      >
        <Flex gap={8} p={10} wrap={'wrap'} pr={'100px'} w={'100%'}>
        </Flex>
      </PromptField>
    </Flex>
  );
};

export const Kling = observer(Element);